import { useEffect, useMemo } from 'react'
import { Input, Stack } from '@chakra-ui/react'

import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import validateEntityOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateEntityOnCustomForm'
import CustomFormUnifiedPersonSelectionDrawer from './views/CustomFormUnifiedPersonSelectionDrawer'
import CustomFormCommonEntitySelectionDrawer from './components/CustomFormCommonEntitySelectionDrawer'
import CustomFormJobsiteSelectionDrawer from './views/CustomFormJobsiteSelectionDrawer'
import CustomFormRoleSelectionDrawer from './views/CustomFormRoleSelectionDrawer'
import CustomFormSalarySelectionDrawer from './views/CustomFormSalarySelectionDrawer'
import CustomFormProfessionalContractTypeSelectionDrawer from './views/CustomFormProfessionalContractTypeSelectionDrawer'
import CustomFormCostCenterSelectionDrawer from './views/CustomFormCostCenterSelectionDrawer'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

type KeyStringWithElement = {
  [key: string]: JSX.Element
}

export default function CustomFormEntitiesQuestions({
  formMethods,
  question,
  questionKey,
  disabled,
}: CustomFormViewerQuestionProps) {
  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const entity: string[] = formMethods?.watch ? formMethods.watch(questionKey) : []

  useEffect(() => {
    if (entity && entity.length > 0) {
      const errorMessage = validateEntityOnCustomForm({ entity, isRequired: question.is_required })

      if (errorMessage === '') {
        removeErrorFromQuestion(question.slug)
      } else {
        setErrorOnQuestion(question, errorMessage)
      }
    }
  }, [entity])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  const RenderEntityComponent = useMemo(() => {
    const possibleEntities: KeyStringWithElement = {
      UNIFIED_PERSON: (
        <CustomFormUnifiedPersonSelectionDrawer question={question} disabled={disabled} questionKey={questionKey} />
      ),
      JOBSITE: <CustomFormJobsiteSelectionDrawer question={question} disabled={disabled} questionKey={questionKey} />,
      ROLE: <CustomFormRoleSelectionDrawer question={question} disabled={disabled} questionKey={questionKey} />,
      SALARY: <CustomFormSalarySelectionDrawer question={question} disabled={disabled} questionKey={questionKey} />,
      COST_CENTER: (
        <CustomFormCostCenterSelectionDrawer question={question} disabled={disabled} questionKey={questionKey} />
      ),
      PROFESSIONAL_CONTRACT_TYPE: (
        <CustomFormProfessionalContractTypeSelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
        />
      ),
      CITY: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          defaultView="SEARCH_ENTITY"
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{ key: 'city', keyPlural: 'cities', label: 'cidade', labelPlural: 'cidades' }}
        />
      ),
      LEVEL_OF_EDUCATION: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{
            key: 'education_level',
            keyPlural: 'education-levels',
            label: 'grau de instrução',
            labelPlural: 'graus de instrução',
          }}
        />
      ),
      DEGREES_OF_KINSHIP: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{
            key: 'kinship',
            keyPlural: 'kinships',
            label: 'grau de parentesco',
            labelPlural: 'graus de parantesco',
          }}
        />
      ),

      CIVIL_STATE: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{
            key: 'civil_state',
            keyPlural: 'civil-states',
            label: 'estado civil',
            labelPlural: 'estados civis',
          }}
        />
      ),
      STATE: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{ key: 'state', keyPlural: 'states', label: 'estado', labelPlural: 'estados' }}
        />
      ),
      ORGANIZATIONAL_UNIT: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{
            key: 'org_unit',
            keyPlural: 'organizational-units',
            label: 'unidade',
            labelPlural: 'unidades',
          }}
        />
      ),

      RACE: (
        <CustomFormCommonEntitySelectionDrawer
          question={question}
          disabled={disabled}
          questionKey={questionKey}
          currentEntity={{
            key: 'race_color',
            keyPlural: 'race-colors',
            label: 'etnia/raça/cor',
            labelPlural: 'etnia/raça/cor',
          }}
        />
      ),
    }

    return possibleEntities[question.entity as string]
  }, [question.entity])

  return (
    <Stack id={question.slug}>
      {RenderEntityComponent}

      <Input isDisabled hidden {...(formMethods?.register && formMethods.register(questionKey, { value: [] }))} />

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />
    </Stack>
  )
}
